import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Card,
  CardMedia,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Alert,
  Fade,
} from "@mui/material";
import {
  Close,
  Refresh,
  Check,
  Image as ImageIcon,
  AutoAwesome,
} from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { getHeader } from "../utils/util";

const AIImageGeneratorDialog = ({ open, onClose, onImageSelect }) => {
  const [prompt, setPrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/agent/generate-assistant`,
        { message: prompt, type: "imageAssistant" },
        getHeader()
      );
      setGeneratedImage(response.data);
    } catch (err) {
      setError("Failed to generate image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const mockAPICall = (promptText) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          fileName: `ai_image_${Date.now()}.png`,
          url: `https://picsum.photos/512/512?random=${Date.now()}&grayscale&blur=2`,
        });
      }, 2000);
    });
  };

  const handleUseImage = () => {
    if (generatedImage && onImageSelect) {
      onImageSelect(generatedImage);
    }
    handleClose();
  };

  const handleGenerateAnother = () => {
    setGeneratedImage(null);
    setPrompt("");
    setError("");
  };

  const handleClose = () => {
    setPrompt("");
    setGeneratedImage(null);
    setError("");
    setLoading(false);
    onClose();
  };

  const suggestedPrompts = [
    "A futuristic cityscape at sunset",
    "Majestic dragon in a fantasy landscape",
    "Abstract geometric patterns in vibrant colors",
    "Serene mountain lake with reflection",
    "Cyberpunk street market at night",
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesome sx={{ color: "white" }} />
            <Typography
              variant="h6"
              component="div"
              color="white"
              fontWeight="600"
            >
              AI Image Generator
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Prompt Input */}
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Describe the image you want to generate..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#667eea",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#667eea",
                },
              },
            }}
          />

          {/* Suggested Prompts */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="white"
              sx={{ mb: 1, opacity: 0.8 }}
            >
              Try these prompts:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {suggestedPrompts.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  size="small"
                  onClick={() => setPrompt(suggestion)}
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Generate Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleGenerateImage}
            disabled={loading || !prompt.trim()}
            startIcon={
              loading ? <CircularProgress size={20} /> : <AutoAwesome />
            }
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
              boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
              fontSize: "1rem",
              fontWeight: "600",
              "&:hover": {
                background: "linear-gradient(45deg, #FE6B8B 40%, #FF8E53 100%)",
                boxShadow: "0 4px 8px 2px rgba(255, 105, 135, .4)",
              },
            }}
          >
            {loading ? "Generating..." : "Generate Image"}
          </Button>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Generated Image Preview */}
          {generatedImage && (
            <Fade in={true} timeout={500}>
              <Card
                sx={{
                  mt: 3,
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: 3,
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    image={generatedImage.url}
                    alt="Generated AI image"
                    sx={{
                      height: 300,
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: "0.75rem",
                    }}
                  >
                    AI Generated
                  </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {generatedImage.fileName}
                  </Typography>
                </Box>
              </Card>
            </Fade>
          )}

          {/* Loading State */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 3,
                p: 4,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderRadius: 3,
                color: "white",
              }}
            >
              <CircularProgress sx={{ color: "white", mb: 2 }} />
              <Typography variant="body1" align="center">
                Creating your masterpiece...
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{ opacity: 0.8, mt: 1 }}
              >
                This may take a few moments
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Action Buttons */}
      {(generatedImage || loading) && (
        <DialogActions
          sx={{
            p: 3,
            gap: 1,
            background: "rgba(255, 255, 255, 0.05)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Cancel
          </Button>

          {generatedImage && (
            <>
              <Button
                onClick={handleGenerateAnother}
                startIcon={<Refresh />}
                sx={{
                  color: "white",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Generate Another
              </Button>
              <Button
                onClick={handleUseImage}
                variant="contained"
                startIcon={<Check />}
                sx={{
                  background:
                    "linear-gradient(45deg, #4CAF50 30%, #45a049 90%)",
                  color: "white",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #4CAF50 40%, #45a049 100%)",
                  },
                }}
              >
                Use This Image
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AIImageGeneratorDialog;
